import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { SettingsAllAPI } from './settings.model';
import Util, { saveLocalStorage } from '../../../util/Util';
import SettingsCalls from './SettingsCalls';
import convertPromiseToQueryReturnValue from '../../../axios/convertPromiseToQueryReturnValue';
import { LocalStorageKeys } from '../../../util/SharedUtils';

export const SettingsAPI = createApi({
  reducerPath: 'settings',
  tagTypes: ['settings-data'],
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    // initialized on src\redux\featureToggle\thunks.ts
    getAllSettings: builder.query<SettingsAllAPI, void>({
      query: () => ({
        url: `${new Util().getSettingsServiceURL()}`,
        method: 'GET',
      }),
      transformResponse: (response: SettingsAllAPI) => response,
      // save temperatureUnitPreferred in cookies to use in proxy
      async onQueryStarted(arg, api) {
        await api.queryFulfilled;
        const { data } = SettingsAPI.endpoints.getAllSettings.select()(
          api.getState() as any
        );
        if (data) {
          // Save the temperature unit preference in cookies
          const temperatureUnit = data.temperatureUnitPreferred;
          saveLocalStorage(
            LocalStorageKeys.temperatureUnitPreferred,
            temperatureUnit ?? '',
            '/'
          );
        }
      },
      providesTags: ['settings-data'],
    }),
    setSetting: builder.mutation<void, { settingName: string; value: string }>({
      query: ({ settingName, value }) => ({
        url: `${new Util().getSettingsServiceURL()}/${settingName}`,
        method: 'PUT',
        data: value,
        headers: {
          'Content-Type': 'text/plain',
        },
      }),
      invalidatesTags: ['settings-data'],
    }),
    setManySettings: builder.mutation<
      void,
      { settings: Record<string, string> }
    >({
      async queryFn({ settings }, _api, _extraOptions, baseQuery) {
        const settingCalls = new SettingsCalls();
        const promises = Object.entries(settings).map(
          async ([settingName, value]) => {
            // Get current state value for the setting
            const state: any = SettingsAPI.endpoints.getAllSettings.select()(
              _api.getState() as any
            );
            const currentValue = state.data?.[settingName];
            // Only update if the value is different
            if (currentValue === value) {
              return Promise.resolve();
            }
            // if the value is false, then delete
            if (!value) {
              return settingCalls.deleteSetting(settingName);
            }
            return settingCalls.setSetting(settingName, value);
          }
        );
        return convertPromiseToQueryReturnValue<void>(
          Promise.all(promises).then()
        );
      },
      invalidatesTags: ['settings-data'],
    }),
  }),
});

export const { useGetAllSettingsQuery, useSetSettingMutation } = SettingsAPI;
